/**
 * One card for every way a page can fail to be a page — and one way forward on it.
 *
 * `DESIGN.md` P7: "Empty states are **one line**, not a bordered panel with an icon
 * and a three-sentence paragraph." An error state is the same shape, and R1-25 is
 * what the old one looked like without that rule: `NotFound` · "Not found" ·
 * *detail* · `req_id 01a06faf-…` · **[Try again]** — two developer artefacts, a
 * button that could never work, and no way back.
 *
 * So the card carries a cap, a sentence, and exactly one control. What it never
 * carries is the problem `code` or the request id: a `DomainErrorCode` is a token
 * for an agent to branch on (`ARCHITECTURE.md` §Guardrails) and a `req_id` is a
 * database key, and neither is something a paying client should be reading.
 *
 * ## Where the request id went
 *
 * It is still the string a support conversation starts with, so it is still here —
 * as `data-request-id` on the section, and as one `console.error` from
 * `RouteError`. Both are one keystroke away for whoever is looking for them and
 * cost the reader nothing. A `<details>` disclosure was the other candidate and is
 * refused for the reason the rest of this file exists: a fold is still chrome on
 * the page, it appears on every error card whether or not anybody wants it, and P7
 * says chrome must earn its place. A screenshot of the DOM and a copy of the
 * console are what a bug report already carries.
 *
 * ## The tab
 *
 * The card sets `document.title`, which is R1-25's fourth complaint: the page had
 * already claimed the topbar slot before its query failed, so the tab went on
 * saying *"January 2099 — Far East Studios Client portal"* about a month that has
 * no report. `<PageTitle>` cannot be used here — it throws outside a
 * `TopbarSlotProvider`, and the root boundary has no shell around it — so this is
 * `useDocumentTitle`, which `shell/useBodyClass.ts` documents as the hook for "a
 * page with no topbar". The format is `shell/nav.ts` `documentTitle` either way,
 * and the topbar's own effect never overwrites it: it only writes when a page has
 * claimed a title, and the page that failed released its claim on the way out.
 */

import { Link, useRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { homeFor } from '../auth/guards.js'
import { usePrincipal } from '../auth/principal.js'
import { documentTitle, type Portal } from '../shell/nav.js'
import { useDocumentTitle } from '../shell/useBodyClass.js'

export type ErrorCardProps = {
  /**
   * The `.hero-cap` line, for the two cards that have a way back rather than a
   * retry. It is the HTTP status and nothing else — `404` is a word the whole web
   * shares, and `NotFound` is a token from our own union.
   */
  readonly cap?: string | undefined
  readonly title: string
  readonly detail: string
  /** `instance` from the problem body. An attribute, never a sentence. */
  readonly requestId?: string | null | undefined
  /** The one control: `<TryAgain>` or `<BackToPortal>`, never both. */
  readonly children?: ReactNode
}

export function ErrorCard({ cap, title, detail, requestId, children }: ErrorCardProps) {
  useErrorDocumentTitle(title)

  return (
    <section
      className="stack"
      role="alert"
      aria-live="polite"
      {...(requestId === null || requestId === undefined ? {} : { 'data-request-id': requestId })}
    >
      <div className="stack stack--tight">
        {cap === undefined ? null : <div className="hero-cap">{cap}</div>}
        <h2>{title}</h2>
        <p className="prose">{detail}</p>
      </div>
      {children === undefined ? null : <div className="btn-row">{children}</div>}
    </section>
  )
}

/**
 * The way back, for a failure that will fail the same way for ever.
 *
 * `homeFor` is the same ladder the guards use and there is one of it: "home" is a
 * different place for staff and for a client, and for nobody at all it is the
 * sign-in screen.
 */
export function BackToPortal() {
  return (
    <Link className="btn" to={homeFor(usePrincipal())}>
      Back to the portal
    </Link>
  )
}

/**
 * The way forward, for a failure that might not happen twice — and it has to
 * actually try.
 *
 * `reset` alone does not. TanStack Router hands the boundary its own reset, which
 * clears the `CatchBoundary` and re-renders the match — and the match still holds
 * the error it stored, so the same card comes straight back and nothing was
 * re-requested. That is fine for a component that threw while rendering and wrong
 * for everything that throws in a `beforeLoad`, which is where the session read
 * lives: a gateway that has come back would never be noticed. `router.invalidate()`
 * is what re-runs the loaders; the two together are the button people think they
 * are pressing.
 */
export function TryAgain({ reset }: { readonly reset: () => void }) {
  const router = useRouter()

  return (
    <button
      type="button"
      className="btn"
      onClick={() => {
        reset()
        void router.invalidate()
      }}
    >
      Try again
    </button>
  )
}

/**
 * The tab, for a page that never rendered.
 *
 * The portal comes from the principal rather than from the path, because
 * `homeFor` is already the one place that ladder is written and a second reading
 * of `pathname.startsWith('/studio')` is a second place for it to be wrong.
 */
export function useErrorDocumentTitle(title: string): void {
  const home = homeFor(usePrincipal())
  const portal: Portal = home === '/studio' ? 'studio' : 'client'
  useDocumentTitle(documentTitle(portal, title))
}
